import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Play, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { useProfile } from "../context/ProfileContext";
import { MASCOTS, IMAGES } from "../data/gameData";

export default function ProfileSelect() {
  const navigate = useNavigate();
  const { profile, selectProfile, loading } = useProfile();
  const [profiles, setProfiles] = useState([]);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [mascot, setMascot] = useState("lumi");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!loading && profile) navigate("/hub");
  }, [loading, profile, navigate]);

  useEffect(() => {
    api.listProfiles().then(setProfiles).catch(() => {
      setError("The game server is unavailable. Please try again in a moment.");
    });
  }, []);

  const enter = (p) => { selectProfile(p); navigate("/hub"); };

  const create = async () => {
    if (!name.trim()) return;
    setBusy(true);
    setError("");
    try {
      const p = await api.createProfile({ name: name.trim(), mascot });
      enter(p);
    } catch (e) {
      setError("Could not create your player. Please try again in a moment.");
    } finally { setBusy(false); }
  };

  return (
    <div className="mr-bg mr-noise min-h-screen relative overflow-hidden">
      <div className="absolute inset-0 opacity-30"
           style={{ backgroundImage: `url(${IMAGES.heroBg})`, backgroundSize: "cover", backgroundPosition: "center" }} />
      <div className="relative z-10 max-w-lg mx-auto px-5 pt-14 pb-10">
        <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="text-center mb-8">
          <div className="inline-flex items-center gap-1.5 text-xs font-bold text-[#FFD600] mb-2 px-3 py-1 rounded-full glass">
            <Sparkles size={13} /> Multiplication Adventure
          </div>
          <h1 className="text-6xl font-black tracking-tight leading-none">
            <span style={{ color: "#FF2A7A" }}>Math</span>
            <span style={{ color: "#00E5FF" }}> Rush</span>
            <span className="text-[#FFD600]">!</span>
          </h1>
          <p className="text-[#A8B2D1] mt-3 text-sm">
            Battle, race &amp; blast your way to mastering tables 2–20.
          </p>
        </motion.div>

        {!creating && (
          <div className="space-y-3">
            {profiles.map((p) => {
              const m = MASCOTS[p.mascot] || MASCOTS.lumi;
              return (
                <button
                  key={p.id}
                  data-testid={`profile-card-${p.id}`}
                  onClick={() => enter(p)}
                  className="w-full glass rounded-3xl p-3 flex items-center gap-3 press text-left">
                  <div className="w-14 h-14 rounded-full overflow-hidden border-2 shrink-0"
                       style={{ borderColor: m.color }}>
                    <img src={m.img} alt={m.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1">
                    <div className="font-bold text-lg">{p.name}</div>
                    <div className="text-xs text-[#A8B2D1]">Level {p.level} · {m.name}</div>
                  </div>
                  <Play size={22} className="text-[#FF2A7A]" />
                </button>
              );
            })}

            <button
              data-testid="new-profile-btn"
              onClick={() => setCreating(true)}
              className="w-full btn-3d py-4 flex items-center justify-center gap-2 text-white font-bold"
              style={{ background: "linear-gradient(90deg,#FF2A7A,#A855F7)", boxShadow: "0 6px 0 rgba(0,0,0,.35)" }}>
              <Plus size={20} /> New Player
            </button>
          </div>
        )}

        {creating && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                      className="glass rounded-3xl p-5">
            <label className="text-sm font-bold text-[#A8B2D1]">Your name</label>
            <input
              data-testid="name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={20}
              placeholder="e.g. Zoe"
              className="w-full mt-2 mb-5 bg-white/5 border border-white/15 rounded-2xl px-4 py-3 text-lg font-bold outline-none focus:border-[#FF2A7A]"
            />
            <div className="text-sm font-bold text-[#A8B2D1] mb-2">Choose your coach</div>
            <div className="grid grid-cols-3 gap-3 mb-6">
              {Object.values(MASCOTS).map((m) => (
                <button
                  key={m.id}
                  data-testid={`mascot-select-${m.id}`}
                  onClick={() => setMascot(m.id)}
                  className="rounded-2xl p-2 press border-2 transition-colors"
                  style={{ borderColor: mascot === m.id ? m.color : "transparent",
                           background: mascot === m.id ? `${m.color}22` : "rgba(255,255,255,0.04)" }}>
                  <div className="w-full aspect-square rounded-xl overflow-hidden mb-1">
                    <img src={m.img} alt={m.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="text-xs font-bold">{m.name}</div>
                  <div className="text-[10px] text-[#A8B2D1] leading-tight">{m.role}</div>
                </button>
              ))}
            </div>
            <div className="flex gap-3">
              <button
                data-testid="create-profile-btn"
                disabled={busy || !name.trim()}
                onClick={create}
                className="flex-1 btn-3d py-3.5 text-white font-bold disabled:opacity-40"
                style={{ background: "#FF2A7A", boxShadow: "0 6px 0 rgba(0,0,0,.35)" }}>
                {busy ? "Creating…" : "Start Playing"}
              </button>
              <button onClick={() => setCreating(false)}
                      className="btn-3d px-5 glass text-white font-bold">Back</button>
            </div>
          </motion.div>
        )}
        {error && <p role="alert" className="mt-4 rounded-xl border border-red-400/40 bg-red-950/70 p-3 text-sm text-red-100">{error}</p>}
      </div>
    </div>
  );
}
